import useSWR from 'swr';
import denApi from '@/lib/api/denApi';
import type { GuardianTransactionResponse } from '@mls-wallet/shared-types';
import { isGuardianTransactionFinished } from '@mls-wallet/shared-types';

const GUARDIAN_TRANSACTION_POLL_INTERVAL_MS = 1500;

function getGuardianTransactionRefreshInterval(
  latestData?: Pick<GuardianTransactionResponse, 'status'>
) {
  return latestData && isGuardianTransactionFinished(latestData.status)
    ? 0
    : GUARDIAN_TRANSACTION_POLL_INTERVAL_MS;
}

/**
 * Hook to poll GuardianTransaction status during async proposal execution.
 *
 * Use this after calling an execute endpoint to track the execution progress.
 * The hook polls every 1.5 seconds while the transaction is in progress,
 * and stops polling once a terminal state is reached.
 *
 * @param guardianTransactionId - The ID returned from execute endpoint, or null to disable polling
 * @returns SWR response with GuardianTransaction data and helper properties
 *
 * @example
 * ```tsx
 * const { data, isPolling, isTerminal } = useGuardianTransactionStatus(guardianTxId);
 *
 * if (isPolling) {
 *   return <Spinner />;
 * }
 *
 * if (data?.status === 'COMPLETED') {
 *   return <SuccessMessage />;
 * }
 *
 * if (data?.status === 'FAILED') {
 *   return <ErrorMessage message={data.errorMessage} />;
 * }
 * ```
 */
export function useGuardianTransactionStatus(
  guardianTransactionId: string | null // null during initial loading before transaction ID is available
) {
  const { data, error, isLoading, mutate } =
    useSWR<GuardianTransactionResponse>(
      guardianTransactionId
        ? `guardian/transactions/${guardianTransactionId}`
        : null,
      (url) => denApi.get<GuardianTransactionResponse, void>(url),
      {
        // Poll every 1.5 seconds while transaction is in progress
        refreshInterval: getGuardianTransactionRefreshInterval,
        revalidateOnFocus: false,
      }
    );

  // Derive helper properties for UI convenience
  const isFinished = data ? isGuardianTransactionFinished(data.status) : false;
  const isPolling = !!guardianTransactionId && !isFinished && !error;

  return {
    /** The GuardianTransaction data */
    data,
    /** Whether there was an error fetching */
    error,
    /** Whether the initial fetch is loading */
    isLoading,
    /** Whether the transaction has reached a terminal state (COMPLETED/FAILED/REVERTED/BLOCKED) */
    isFinished,
    /** Whether we're still polling (not terminal and no error) */
    isPolling,
    /** Manually refetch the data */
    refetch: mutate,
  };
}
